import { EventEmitter, Injectable } from '@angular/core';
import { LgvLoanApp } from '../../data-models/lgv-loan-application.model';
import { LgvFormlyModel } from '../../data-models/formly.model';
import { BehaviorSubject, Observable, Subscription, filter, first, map, mergeMap, of, take, takeUntil, tap } from 'rxjs';
import { Router } from '@angular/router';
import { Console, debug } from 'console';
import { HttpClient } from '@angular/common/http';
import { environment as env } from '../../../environments/environment';
import { NotificationService } from '../core.module';
import { LgvLoanApplicationService } from './lgv-loan-application.service';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { JsonLoaderService } from './json-loader.service';
import { Form } from '@angular/forms';
import { LgvModel } from '../../data-models/longevity.model';
import { setProperty } from 'dot-prop';
import { MatDialog } from '@angular/material/dialog';
import { LgvUserService } from './lgv-user.service';
import { LocationService } from './location.service';

@Injectable({
    providedIn: 'root'
})

export class LgvFormUtilityService {

    constructor(private router: Router,
        private lgvLoanAppSvc: LgvLoanApplicationService,
        private jsonLoader: JsonLoaderService,
        private notificationSvc: NotificationService, 
        private http: HttpClient,
        private dialog: MatDialog,
        private lgvUserSvc: LgvUserService,
        private locationService: LocationService) { 

        this.dataModel$.pipe(filter(f=> f !== null)).subscribe(x=>{
            let isApplyingForCB = x.loanApp?.loanInfo?.formCustomFields?.IsApplyingForCoBorrower === true;
            
            if(this.isApplyingForCoBorrower$.value !== isApplyingForCB){
                this.isApplyingForCoBorrower$.next(isApplyingForCB);
                // console.log(`change to isApplyingForCoBorrower$: ${x}`);
                this.manageCoBorrowerStatus(this.isApplyingForCoBorrower$.value);
            }

        });

    }

    init(agentId: string, dataModel: any, schema: LgvFormlyModel.Object[], formType:FormType, clientId:string = null) {
        this.formType = formType;
        this.isLoadingFormState$.next(true);
        this.isLoadingModel$.next(true);

        const schemaWithHooks = this.addFieldHooks(schema);
        this.clearChangeMap();
        this.defaultModel$.next(structuredClone(dataModel));
        this.dataModel$.next(structuredClone(dataModel));
        this.manageHiddenSectionsForFormType(schemaWithHooks);
        this.schema$.next(schemaWithHooks);
        this.agentId = agentId;
        this.clientId = clientId;
        this.userConfirmedNoItemsList$.next([]);
    }

    OnLoanSubmitted: EventEmitter<any> = new EventEmitter(); 
    OnGoToReviewAndSubmit: EventEmitter<any> = new EventEmitter(); 

    objectId:string;
    agentId:string;
    clientId:string;
    formType:FormType;

    loanAppStatus: LgvLoanApp.LoanStatus = null;

    private previouslySavedModel: string = null;
    private previouslySavedFormState: string = null;

    private _changeMap: Map<string, any> = new Map<string, any>();
    changeMap$ = new BehaviorSubject(this._changeMap);

    private _defaultModel: any = {};
    defaultModel$ = new BehaviorSubject(this._defaultModel);

    private _savedLoanApp: LgvLoanApp.LoanApplicationDetail = {};
    savedLoanApp$ = new BehaviorSubject(this._savedLoanApp);

    private _dataModel: any = {};
    dataModel$ = new BehaviorSubject(this._dataModel);

    private _schema: LgvFormlyModel.Object[] = [];
    schema$ = new BehaviorSubject(this._schema);

    _appNode: LgvFormlyModel.NavigationNode = null;
    appNode$ = new BehaviorSubject(this._appNode);

    private _curLevel1: number = -1;
    curLevel1$ = new BehaviorSubject(this._curLevel1);

    private _curLevel2: number = -1;
    curLevel2$ = new BehaviorSubject(this._curLevel2);

    _isLoadingModel: boolean = true;
    isLoadingModel$ = new BehaviorSubject(this._isLoadingModel);

    _isLoadingFormState: boolean = true;
    isLoadingFormState$ = new BehaviorSubject(this._isLoadingFormState);

    _isLookingFromZip: boolean = false;
    isLookingFromZip$ = new BehaviorSubject(this._isLookingFromZip);

    _terms: any[] = [];
    terms$ = new BehaviorSubject(this._terms);

    _userConfirmedNoItemsList: LgvFormlyModel.UserConfirmedNoItem[] = [];
    userConfirmedNoItemsList$ = new BehaviorSubject(this._userConfirmedNoItemsList);


    isApplyingForCoBorrower$ = new BehaviorSubject(false);
    showRespaTrigger$ = new BehaviorSubject(false);
    fifteenMinuteTimerEnd$ = new BehaviorSubject(false);

    hasEnteredConsumerFirstName = false;
    hasEnteredConsumerLastName = false;
    hasEnteredSSN= false;
    hasEnteredStreet = false;
    hasEnteredCity = false;
    hasEnteredZip = false;
    hasEnteredAppraisalValue = false;
    hasEnteredLoanAmount = false;
    hasEnteredIncomeName = false;
    hasEnteredIncomeAmount = false;
 
    get formlyOptions$():Observable<FormlyFormOptions> { return this.dataModel$.pipe(filter(f=> !!f),
        // tap(t=> { console.log('tap from formlyOptions$:'); console.log(t)}), 
        map(x=> { 
        return { formState: { mainModel: x } } 
        }    
     ))} 

    get isPreApplicationMode():boolean { return this.loanAppStatus && this.loanAppStatus.code === 'PreApplication'; }
    get isSubmittedCanStillEditMode():boolean { return this.loanAppStatus && !this.isPreApplicationMode && this.loanAppStatus.code.startsWith('Pre'); }
    get isReadOnlyMode():boolean { return this.loanAppStatus && !this.isPreApplicationMode && !this.isSubmittedCanStillEditMode; }
    get isShortApp():boolean{
        let model = this.dataModel$.value;
        let customFields = model?.loanApp?.loanInfo?.formCustomFields;
        if(customFields && customFields['IsShortApp']){
            return customFields['IsShortApp'];
        }
        else{
            return false;
        }
    }

    // ---------------- TEMP Hard Coding Status for Testing --------------------------
    // get isPreApplicationMode():boolean { return false; }
    // get isSubmittedCanStillEditMode():boolean { return true; }
    // ---------------- TEMP Hard Coding Status for Testing --------------------------


    get saveOnEachNavigation():boolean { return this.isPreApplicationMode; }
 
    //#region Properties
    get parentAppObj$(): Observable<LgvFormlyModel.Object> {
        return this.appNode$.pipe(filter(f => !!f), map((x: LgvFormlyModel.NavigationNode) => x.parent))
    }

    get appObj$(): Observable<LgvFormlyModel.Object> {
        return this.appNode$.pipe(filter(f => !!f), map((x: LgvFormlyModel.NavigationNode) => x.appObj))
    }
    get pageTitle$(): Observable<string> {
        return this.appObj$.pipe(filter(f => !!f), map((x: LgvFormlyModel.Object) => x?.props?.label ?? null))
    }

    get parentName$(): Observable<string> {
        return this.parentAppObj$.pipe(filter(f => !!f), map((x: LgvFormlyModel.Object) => x?.props?.label ? x.props.label === 'Root' ? 'Home' : x.props.label : null))
    }

    get hasGroups$(): Observable<boolean> {
        return this.appObj$.pipe(filter(f => !!f), map((x: LgvFormlyModel.Object) => {

            if (x?.fieldGroup?.length > 0) {
                for (let obj of x.fieldGroup) {

                    // console.log(`hasGroup$ - ${x?.props?.label}  |  ${obj?.props?.label}   |  ${obj?.type}`);
                    if (!!obj?.fieldGroup && obj?.type !== 'repeatingSection' && obj?.type !== 'repeatingSectionSummary') {
                        return true;
                    }
                }
            }
            return false;
        }));
    }

    get hasRepeatingSectionSummary$(): Observable<boolean> {
        // return this.appObj$.pipe(filter(f => !!f), map((x: LgvFormlyModel.Object) => {
        //     return x.type === 'repeatingSectionSummary' && !!x.fieldArray;
        // }));

        return this.appObj$.pipe(filter(f => !!f), map((x: LgvFormlyModel.Object) => {

            if (x.type === 'repeatingSectionSummary' && !!x.fieldArray) {
                return true;
            }

            if (x?.fieldGroup?.length > 0) {
                for (let obj of x.fieldGroup) {
                    if (obj.type === 'repeatingSectionSummary' && !!obj.fieldArray) {
                        return true;
                    }
                }
            }
            return false;
        }));



    }

    get hasRepeatingItem$(): Observable<boolean> {
        return this.appObj$.pipe(filter(f => !!f), map((x: LgvFormlyModel.Object) => {

            if (x?.fieldGroup?.length > 0) {
                for (let obj of x.fieldGroup) {
                    if (obj.type === 'repeatingSection' && !!obj.fieldArray) {
                        return true;
                    }
                }
            }
            return false;
        }));
    }

    get hasFields$(): Observable<boolean> {
        return this.appObj$.pipe(filter(f => !!f), map((x: LgvFormlyModel.Object) => {
            if (x?.fieldGroup?.length > 0) {
                for (let obj of x.fieldGroup) {
                    if (!(!!obj?.fieldGroup)) {
                        return true;
                    }
                }
            }
            return false;
        }));
    }

    get hasFieldsOtherThanRepeaters$(): Observable<boolean> {
        return this.appObj$.pipe(filter(f => !!f), map((x: LgvFormlyModel.Object) => {


            if (x?.fieldGroup?.length > 0) {
                for (let obj of x.fieldGroup) {
                    if (!(!!obj?.fieldGroup) && obj?.type !== 'repeatingSection' && obj?.type !== 'repeatingSectionSummary') {
                        return true;
                    }
                }
            }
            return false;
        }));
    }
    //#endregion Properties


    addFieldHooks(schema) {
        let schemaWithHooks = structuredClone(schema);
        let zipField;

        const onSubjectAddressPropertyZipCodeChangeHook = {
            onInit: (field) => {
              return field.options.fieldChanges.pipe(
                filter((e :any)=> e.value?.length >= 5 && e.field?.key == 'loanApp.loanInfo.subjectPropertyAddress.zipCode'),
                tap((e: any) => {
                    this.isLookingFromZip$.next(true);
                    this.lookUpLocationInfoFromZip(e?.value, 'loanApp.loanInfo.subjectPropertyAddress');
                    this.hasEnteredZip = true;
                    this.checkForRESPATrigger();
                }),
              );
            }
          }
        // check the correct mapping of property address for the thype of loan app.
        // (i used this instead of isShortApp property since at this point that has no value yet)
        zipField = schemaWithHooks[0].fieldGroup[1].fieldGroup[2];
        if(zipField.key == 'loanApp.loanInfo.subjectPropertyAddress.zipCode') {
            schemaWithHooks[0].fieldGroup[1].fieldGroup[2] = {
                ...zipField,
                hooks: onSubjectAddressPropertyZipCodeChangeHook
            };
        } else {
            zipField = schemaWithHooks[0].fieldGroup[1].fieldGroup[4];
            schemaWithHooks[0].fieldGroup[1].fieldGroup[4] = {
                ...zipField,
                hooks: onSubjectAddressPropertyZipCodeChangeHook
            };
        }

        //#region setup hooks for 6 pcs of info
        const firstNameField = schemaWithHooks[1].fieldGroup[0].fieldGroup[1];
        if(firstNameField.key == 'loanApp.borrower.firstName') {
            schemaWithHooks[1].fieldGroup[0].fieldGroup[1] = {
                ...firstNameField,
                hooks: {
                    onInit: (field) => {
                    return field.options.fieldChanges.pipe(
                      filter((e :any)=> e.field?.key == 'loanApp.borrower.firstName'),
                      tap((e: any) => {
                          if(e.value?.length > 0) {
                            this.hasEnteredConsumerFirstName = true;
                            this.checkForRESPATrigger();
                          }
                      }),
                    );
                  }
                }
            };
        }

        const lastNameField = schemaWithHooks[1].fieldGroup[0].fieldGroup[2];
        if(lastNameField.key == 'loanApp.borrower.lastName') {
            schemaWithHooks[1].fieldGroup[0].fieldGroup[2] = {
                ...lastNameField,
                hooks: {
                    onInit: (field) => {
                    return field.options.fieldChanges.pipe(
                      filter((e :any)=> e.field?.key == 'loanApp.borrower.lastName'),
                      tap((e: any) => {
                          if(e.value?.length > 0) {
                            this.hasEnteredConsumerLastName = true;
                            this.checkForRESPATrigger();
                          }
                      }),
                    );
                  }
                }
            };
        }

        const incomeName = schemaWithHooks[2]?.fieldArray?.fieldGroup[20]?.fieldArray?.fieldGroup[0];
        if(incomeName?.key == 'name') {
            schemaWithHooks[2].fieldArray.fieldGroup[20].fieldArray.fieldGroup[0] = {
                ...incomeName,
                hooks: {
                    onInit: (field) => {
                    return field.options.fieldChanges.pipe(
                      filter((e :any)=> e.field?.key == 'name'),
                      tap((e: any) => {
                          if(e.value?.length > 0) {
                            this.hasEnteredIncomeName = true;
                            this.checkForRESPATrigger();
                          }
                      }),
                    );
                  }
                }
            };
        }

        const incomeAmount = schemaWithHooks[2]?.fieldArray?.fieldGroup[20]?.fieldArray?.fieldGroup[1];
        if(incomeAmount?.key == 'amount') {
            schemaWithHooks[2].fieldArray.fieldGroup[20].fieldArray.fieldGroup[1] = {
                ...incomeAmount,
                hooks: {
                    onInit: (field) => {
                    return field.options.fieldChanges.pipe(
                      filter((e :any)=> e.field?.key == 'amount'),
                      tap((e: any) => {
                          if(e.value?.length > 0) {
                            this.hasEnteredIncomeAmount = true;
                            this.checkForRESPATrigger();
                          }
                      }),
                    );
                  }
                }
            };
        }


        const ssnField = schemaWithHooks[1].fieldGroup[0].fieldGroup[9];
        if(ssnField.key == 'loanApp.borrower.ssn') {
            schemaWithHooks[1].fieldGroup[0].fieldGroup[9] = {
                ...ssnField,
                hooks: {
                    onInit: (field) => {
                    return field.options.fieldChanges.pipe(
                      filter((e :any)=> e.field?.key == 'loanApp.borrower.ssn'),
                      tap((e: any) => {
                          if(e.value?.length > 0) {
                            this.hasEnteredSSN = true;
                            this.checkForRESPATrigger();
                          }
                      }),
                    );
                  }
                }
            };
        }

        const streetAddressField = schemaWithHooks[0].fieldGroup[1].fieldGroup[1];
        if(streetAddressField.key == 'loanApp.loanInfo.subjectPropertyAddress.street') {
            schemaWithHooks[0].fieldGroup[1].fieldGroup[1] = {
                ...streetAddressField,
                hooks: {
                    onInit: (field) => {
                    return field.options.fieldChanges.pipe(
                      filter((e :any)=> e.field?.key == 'loanApp.loanInfo.subjectPropertyAddress.street'),
                      tap((e: any) => {
                          if(e.value?.length > 0) {
                            this.hasEnteredStreet = true;
                            this.checkForRESPATrigger();
                          }
                      }),
                    );
                  }
                }
            };
        }

        const cityField = schemaWithHooks[0].fieldGroup[1].fieldGroup[2];
        if(cityField.key == 'loanApp.loanInfo.subjectPropertyAddress.city') {
            schemaWithHooks[0].fieldGroup[1].fieldGroup[2] = {
                ...cityField,
                hooks: {
                    onInit: (field) => {
                    return field.options.fieldChanges.pipe(
                      filter((e :any)=> e.field?.key == 'loanApp.loanInfo.subjectPropertyAddress.city'),
                      tap((e: any) => {
                          if(e.value?.length > 0) {
                            this.hasEnteredCity = true;
                            this.checkForRESPATrigger();
                          }
                      }),
                    );
                  }
                }
            };
        }

        const appraisalValueField = schemaWithHooks[0].fieldGroup[0].fieldGroup[2];
        if(appraisalValueField.key == 'loanApp.loanInfo.appraisalValue') {
            schemaWithHooks[0].fieldGroup[0].fieldGroup[2] = {
                ...appraisalValueField,
                hooks: {
                    onInit: (field) => {
                    return field.options.fieldChanges.pipe(
                      filter((e :any)=> e.field?.key == 'loanApp.loanInfo.appraisalValue'),
                      tap((e: any) => {
                          if(e.value?.length > 0) {
                            this.hasEnteredAppraisalValue = true;
                            this.checkForRESPATrigger();
                          }
                      }),
                    );
                  }
                }
            };
        }

        const loanAmountField = schemaWithHooks[0].fieldGroup[0].fieldGroup[4];
        if(loanAmountField.key == 'loanApp.loanInfo.loanAmount') {
            schemaWithHooks[0].fieldGroup[0].fieldGroup[4] = {
                ...loanAmountField,
                hooks: {
                    onInit: (field) => {
                    return field.options.fieldChanges.pipe(
                      filter((e :any)=> e.field?.key == 'loanApp.loanInfo.loanAmount'),
                      tap((e: any) => {
                          if(e.value?.length > 0) {
                            this.hasEnteredLoanAmount = true;
                            this.checkForRESPATrigger();
                          }
                      }),
                    );
                  }
                }
            };
        }
        //#endregion
        
        return schemaWithHooks;
    }

    initRespaChecks() {
        this.showRespaTrigger$.next(false);
        this.fifteenMinuteTimerEnd$.next(false);
        this.hasEnteredConsumerFirstName = !!this.dataModel$?.value?.loanApp?.borrower?.firstName;
        this.hasEnteredConsumerLastName = !!this.dataModel$?.value?.loanApp?.borrower?.lastName;
        this.hasEnteredSSN = !!this.dataModel$?.value?.loanApp?.borrower?.ssn;
        this.hasEnteredStreet = !!this.dataModel$?.value?.loanApp?.loanInfo?.subjectPropertyAddress.street;
        this.hasEnteredCity = !!this.dataModel$?.value?.loanApp?.loanInfo?.subjectPropertyAddress.city;
        this.hasEnteredZip = !!this.dataModel$?.value?.loanApp?.loanInfo?.subjectPropertyAddress.zipCode;
        this.hasEnteredAppraisalValue = !!this.dataModel$?.value?.loanApp?.loanInfo?.appraisalValue;
        this.hasEnteredLoanAmount = !!this.dataModel$?.value?.loanApp?.loanInfo?.loanAmount;
    }

    checkForRESPATrigger() {
        if (this.hasEnteredConsumerFirstName &&
            this.hasEnteredConsumerLastName && 
            this.hasEnteredSSN && 
            this.hasEnteredStreet &&
            this.hasEnteredCity && 
            this.hasEnteredZip && 
            this.hasEnteredAppraisalValue && 
            this.hasEnteredLoanAmount && 
            this.hasEnteredIncomeName &&
            this.hasEnteredIncomeAmount
        ) {
            setTimeout(() => {
                this.showRespaTrigger$.next(true);
            }, 7000)
        }
    }

    
    lookUpLocationInfoFromZip(zipCode: string, addressField: string) {
        this.isLookingFromZip$.next(true);
        
        // Get userId from the user service
        const userId = this.lgvUserSvc?.DBUserSS$?.value?.agentInfo?.id;
        
        this.locationService.lookupLocationByZip(zipCode, userId)
            .subscribe(
                locationInfo => {
                    if (locationInfo) {
                        const model = structuredClone(this.dataModel$.value);
                        setProperty(model, addressField + '.state', locationInfo.stateCode);
                        setProperty(model, addressField + '.city', locationInfo.city);
                        this.dataModel$.next(model);
                        setTimeout(() => {
                            this.isLookingFromZip$.next(false);
                        }, 500);
                    } else {
                        // Handle no locations returned (user not licensed in this state)
                        this.notificationSvc.error(`No licensed locations found for zipcode ${zipCode}. Please verify you are licensed in this state.`);
                        setTimeout(() => {
                            this.isLookingFromZip$.next(false);
                        }, 500);
                    }
                },
                error => {
                    this.notificationSvc.error(`Error retrieving location data for zipcode ${zipCode}`);
                    console.error('Error looking up location data:', error);
                    setTimeout(() => {
                        this.isLookingFromZip$.next(false);
                    }, 500);
                }
            );
    }


    triggerDataModelRefresh(){
        this.dataModel$.next(this.dataModel$.value);
    }

    setCurrentLevels(lvl1: number, lvl2: number) {
        this.curLevel1$.next(lvl1);
        this.curLevel2$.next(lvl2);
        this.appNode$.next(this.getThisFieldGroupObj(lvl1, lvl2));
    }

    getThisFieldGroupObj(lvl1: number = -1, lvl2: number = -1): LgvFormlyModel.NavigationNode {

        let root = {
            parent: null,
            appObj: {
                props: { label: 'Root' },
                fieldGroup: this.schema$.value
            }
        }

        if (lvl1 === -1) {
            return root;
        }

        let lvl1Model = this.schema$.value[lvl1];

        if (lvl2 >= 0 && lvl1Model?.fieldGroup && lvl1Model?.fieldGroup.length >= 0) {
            return {
                parent: lvl1Model,
                appObj: lvl1Model.fieldGroup[lvl2]
            }
        }
        else {
            return {
                parent: root.appObj,
                appObj: lvl1Model
            }
        }
    }

    //#region Navigation
    private getRelativeUrl(): string {
        let location = window.location.href;
        let parts = location.split('/');

        if (parts.length > 3) {

            let lastPart = parts[parts.length - 1];
            let secondToLastPart = parts[parts.length - 2];
            let haveCurNavigationLevels = (+lastPart) >= -1 && (+secondToLastPart) >= -1; // test to see if last two parts are numbers

            let markerIndex = location.indexOf('#');
            if (markerIndex > -1) {
                markerIndex++; //move past #
                let relativeUrl = location.substring(markerIndex);

                let rtnVal = '';
                if (haveCurNavigationLevels) {
                    let currentLevels = `/${parts[parts.length - 2]}/${parts[parts.length - 1]}`;
                    rtnVal =  relativeUrl.replace(currentLevels, '');
                }
                else {
                    rtnVal = relativeUrl;
                }

                return rtnVal;
            }
        }

        console.log('Unexpected path error');
        return location;
    }

    navigate(lvl1: number, lvl2: number) {
        if(this.saveOnEachNavigation){
            this.saveForm();
        }

        if(lvl1 === -2){
            // -2 is a hack to 1) navigate to home, then 2) go to review & submit mode
            let newUrl = `${this.getRelativeUrl()}/-1/-1`;
            this.router.navigate([newUrl]);
            this.OnGoToReviewAndSubmit.emit(true); 
        }
        else{
            let newUrl = `${this.getRelativeUrl()}/${lvl1}/${lvl2}`;
            this.router.navigate([newUrl]);
        }
    }

    navigateToParentNode() {
        if (this.curLevel2$.value > -1) {
            this.navigate(this.curLevel1$.value, -1);
        }
        else {
            this.returnHome();
        }
    }

    navigateToPreviousSection(){
        if(this.curLevel2$.value > -1){
            this.navigate(this.curLevel1$.value, this.curLevel2$.value -1);
            return;
        }

        if(this.curLevel1$.value > -1){
            this.navigate(this.curLevel1$.value -1, -1);
            return;
        }

        this.returnHome();
    }

    navigateToSectionSummary(){
        this.navigate(this.curLevel1$.value, -1);
    }

    navigateToNextSection(){
        let node = this.appNode$.value;
        // console.log(node);

        let isRepeater = false;

        let thisObj = this.getThisFieldGroupObj(this.curLevel1$.value, this.curLevel2$.value);
        if(thisObj && thisObj.appObj){
            isRepeater = thisObj.appObj.type === 'repeatingSectionSummary';
        }

        let nextLevel2Obj = this.getThisFieldGroupObj(this.curLevel1$.value, this.curLevel2$.value +1);

        let haveNextLevel2 = nextLevel2Obj 
            && nextLevel2Obj.appObj 
            && nextLevel2Obj.appObj.fieldGroup 
            && !this.isSummaryLevelHidden(this.curLevel1$.value, this.curLevel2$.value +1);
            
        if(!isRepeater && haveNextLevel2){
            this.navigate(this.curLevel1$.value, this.curLevel2$.value +1);
            return;
        }

        // if we are in a level 2 obj, and don't have another (last one), then return the the level 1 summary instead of going to the next level 1
        if(this.curLevel2$.value > -1 && !haveNextLevel2){
            this.navigateToSectionSummary();
            return;
        }

        // skip over hidden sections
        let targetLevel_1 = this.curLevel1$.value +1;
        while(this.isThisSectionHiddenForType(targetLevel_1, -1)) {
            targetLevel_1++;
        }

        let nextLevel1Obj = this.getThisFieldGroupObj(targetLevel_1, -1);
        if(nextLevel1Obj && nextLevel1Obj.appObj){
            this.navigate(this.curLevel1$.value +1, -1);
            return;
        }

        this.returnHome();
    }

    navigateToNextSectionSummary(){ 
        // skip over hidden sections
        let targetLevel_1 = this.curLevel1$.value +1;
        while(this.isThisSectionHiddenForType(targetLevel_1, -1)) {
            targetLevel_1++;
        }

        // test for end of the line... 
        if(targetLevel_1 > this.schema$.value?.length-1){ 
            this.returnHome(); 
            return 
        } 
 
        this.navigate(targetLevel_1, -1); 
    } 
    
    returnHome() {
        this.navigate(-1, -1);
    }

    //#endregion Navigation

    //#region Utility
    updateModel(model: any) {
        this.dataModel$.next(model);
    }

    clearChangeMap(){
        let cm = this.changeMap$.value;
        cm.clear();
        this.changeMap$.next(cm);
    }

    updateChangeMap(fields: FormlyFieldConfig[], arrayIndex = null){

        let defaultModel = this.defaultModel$.value;

        let fieldStatusList = [];

        for(let field of fields){

            // skip templates and anything w/ out a key (no data)
            if(!field.key){
                continue;
            }

            let arrayItems = null;
            let numArrayItems = 0;
            let isRepeatingType = field.type.toString().startsWith('repeatingSection');

            if(field.fieldArray && field?.type === 'repeatingSection'){
                numArrayItems = field?.fieldGroup.length;

                // cache values for summary report
                arrayItems = [];
                for(let arrayItem of field?.fieldGroup){
                    if(arrayItem?.formControl){
                        arrayItems.push(arrayItem.formControl.value);
                    }
                }
            }

            if(field.fieldArray && field?.type === 'repeatingSectionSummary'){
                // sectionSummary already shows items for summary report.  No need to cache.
                numArrayItems = field?.fieldGroup.length;
            }
    


            let haveMissingArrayItemsError = false;
            if(isRepeatingType && numArrayItems === 0 && field.props['confirmIfNoItems']){
                // if field has confirmIfNoItems, then we need to see if user gave confirmation (stored in userConfirmedNoItemsList$)
                let changeSummaryKey = this.buildChangeMapKey(this.curLevel1$.value, this.curLevel2$.value, arrayIndex);
                let haveUserConfirmedNoItems = this.haveUserConfirmedNoItems({changeSummaryKey:changeSummaryKey, modelKey:field.key.toString()});
                haveMissingArrayItemsError = !haveUserConfirmedNoItems;
            }

            let defaultVal = this.getValueFromModelRecursive(field.key.toString(), defaultModel) ?? null;
            let curVal = field?.formControl?.value;

            let missing = !field.hide && (field?.formControl?.invalid || haveMissingArrayItemsError)
            if(field?.type === "ssnInput" && curVal?.length > 0){
                curVal = '***-**-****';
                missing = false
            }

            let curLabel = this.findLabelForValue(field, curVal);

            fieldStatusList.push( {
                key: field.key.toString(),
                label: field?.props?.label,
                defaultVal: curLabel,
                required: field?.props.required && !field.hide,
                currentVal: curVal,
                currentLabel: curLabel,
                missing: missing,
                changed: defaultVal !== curVal && curVal !== '',
                arrayItems: arrayItems,
                hidden: field.hide
            });
        }

        let numMissing = fieldStatusList.filter(x => x.missing).length;

        let summary: LgvFormlyModel.GroupSummary =  {
            sectionStatus: numMissing === 0 ? LgvFormlyModel.FormSectionStatus.Complete : LgvFormlyModel.FormSectionStatus.Started,
            numFields: fieldStatusList.length,
            numChanged: fieldStatusList.filter(x => x.changed).length,
            numRequiredRemaining: numMissing,
            numRequired: fieldStatusList.filter(x => x.required).length,
            fieldList: fieldStatusList,
            refreshedInApp: true// distinguish betwenn summaries loaded from the db.
        }

        this.setChangeSummaryForCurrentLevel(summary, arrayIndex);
    }


    getValueFromModelRecursive(key: string, obj: LgvFormlyModel.Object): any {

        let firstDotIndex = key.indexOf('.');

        let parts = key.split('.');
        if (parts.length > 1) {

            let remainingKey = key.substr(firstDotIndex + 1);
            let thisKey = key.substr(0, firstDotIndex);
            let thisObj = obj && obj[thisKey];
            if (!thisObj) {
                return null;
            }
            return this.getValueFromModelRecursive(remainingKey, thisObj);
        }
        else {
            if (obj && obj[key] !== null) {
                return obj[key];
            }
            else {
                return null;
            }
        }
    }

    isBlankLabel(label:string): boolean{
        let trimmed = label.replace(/\s/g, "");
        return trimmed.length === 0;
    }

    findLabelForValue(field: FormlyFieldConfig, value: any): string {
        if (field.type === 'select') {
            // Ensure options is treated as an array
            if (Array.isArray(field.props?.options)) {
                const option = field.props.options.find(opt => opt.value === value);
                return option ? option.label : ''; // Fallback label if not found
            } else if (field.props?.options instanceof Observable) {
                // Handle Observable options if necessary (not typical for synchronous operations)
                console.error("Options are provided as an Observable, which can't be handled synchronously.");
                return 'Loading...'; // Or handle asynchronously as needed
            }
        }
        return value; // Return the value if not a select field or no options found
    }
    

    //#endregion Utility

    public updateFromDB(applicationId:string, dbModel: LgvLoanApp.LoanApplicationDetail){
        this.objectId = applicationId;
        this.savedLoanApp$.next(dbModel);
        let formModel = structuredClone(this.dataModel$.value);

        formModel.loanApp.loanInfo = structuredClone(dbModel.loanInfo);

        this.loanAppStatus = dbModel?.loanTimeline?.activeStatus;

        if(dbModel.loanInfo?.borrowers?.length > 0){
            let dbBorrower = dbModel.loanInfo.borrowers[0];

            formModel.loanApp.borrower = structuredClone(dbBorrower);
            formModel.loanApp.borrower = this.mapDemographicsFromDB(formModel.loanApp.borrower);

            // map current owned property to current address fields
            formModel.loanApp.borrower = this.mapCurrentOwnedAddressFromDB(formModel.loanApp.borrower, true);
            const isApplyingForCoBorrower = formModel.loanApp.loanInfo?.customFields?.find(cf=> cf.code === 'IsApplyingForCoBorrower')
            if(isApplyingForCoBorrower?.value == 'true'){
                formModel.loanApp.borrower.coBorrower = this.mapCurrentOwnedAddressFromDB(formModel.loanApp.borrower.coBorrower, false);
                formModel.loanApp.borrower.coBorrower = this.mapDemographicsFromDB(formModel.loanApp.borrower.coBorrower);
                // check if same address with borrower
                if(!!formModel.loanApp.borrower.coBorrower) {
                    formModel.loanApp.borrower.coBorrower.currentAddress = {
                        ...formModel.loanApp.borrower?.coBorrower?.currentAddress,
                        isTheSameAsBorrower: this.isSameAddresses(
                            formModel.loanApp.borrower?.currentAddress,
                            formModel.loanApp.borrower?.coBorrower?.currentAddress
                        )
                    };
                }
                
                
            }

            // merge borrower and co-borrower arrays to temp arrays
            formModel.loanApp.borrower = this.mergeBorrowerAndCoBorrowerArraysFromDB(formModel.loanApp.borrower, 'ownedProperties', 'tempOwnedProperties', 'ownedBy');
            formModel.loanApp.borrower = this.mergeBorrowerAndCoBorrowerArraysFromDB(formModel.loanApp.borrower, 'liabilities', 'tempLiabilities');
            formModel.loanApp.borrower = this.mergeBorrowerAndCoBorrowerArraysFromDB(formModel.loanApp.borrower, 'employments', 'tempEmployments');
            formModel.loanApp.borrower = this.mergeBorrowerAndCoBorrowerArraysFromDB(formModel.loanApp.borrower, 'otherIncomes', 'tempOtherIncomes');
            formModel.loanApp.borrower = this.mergeBorrowerAndCoBorrowerArraysFromDB(formModel.loanApp.borrower, 'assets', 'tempAssets');

            // convert DB employment months to FORM years / months
            if(formModel.loanApp.borrower?.tempEmployments)
            {
                for(let e of formModel.loanApp.borrower.tempEmployments){
                    e.street = e.address?.street;
                    if(+e.lineOfWorkInMonths > 0){
                        let years = Math.floor(+e.lineOfWorkInMonths / 12);
                        let months = +e.lineOfWorkInMonths - (years * 12);
                        e.formWorkYears = years;
                        e.formWorkMonths = months;
                        delete e.lineOfWorkInMonths;
                    }
                }

                // map primary employment to borrower incomeType
                const primaryEmploymenIndex = formModel.loanApp.borrower?.employments?.findIndex(e => e.isPrimary && e.isCurrent);
                
                if(primaryEmploymenIndex >=0) {
                    const isSelfEmployed = formModel.loanApp.borrower.employments[primaryEmploymenIndex]?.isSelfEmployed;
                    formModel.loanApp.borrower.incomeType = isSelfEmployed ? 'selfEmployed' : 'W2-earner';
                }
            }

            delete formModel.loanApp.loanInfo.borrowers;
        }

        if(dbModel.loanInfo?.customFields?.length > 0){
            for(let cf of dbModel.loanInfo.customFields){
                if(!formModel.loanApp.loanInfo.formCustomFields){
                    formModel.loanApp.loanInfo['formCustomFields'] = {};
                }
                if (cf?.value === "Yes" || cf?.value === "true") { 
                    formModel.loanApp.loanInfo.formCustomFields[cf.code] = true; 
                } 
                else if (cf?.value === "No" || cf?.value === "false") { 
                    formModel.loanApp.loanInfo.formCustomFields[cf.code] = false; 
                } 
                else { 
                    formModel.loanApp.loanInfo.formCustomFields[cf.code] = cf.value; 
                }
            }
        }

        if(formModel.loanApp?.loanInfo?.customFields){
            delete formModel.loanApp.loanInfo.customFields;
        }

        // not required for the put.  Makes our model more bulky
        if(formModel.loanApp?.loanInfo?.assignments){
            delete formModel.loanApp.loanInfo.assignments;
        }

        if(formModel.loanApp?.loanInfo?.subjectPropertyAddress?.street === "tbd"){
            formModel.loanApp.loanInfo.subjectPropertyAddress.tbd = true
        } else {
            formModel.loanApp.loanInfo.subjectPropertyAddress.tbd = false
        }

        let bldUrl = `${env.LgvApiBaseUrl}/loanapplications/${applicationId}/formstate`;
        if(this.clientId){
            bldUrl = bldUrl + `?clientId=${this.clientId}`;
        }
        this.http.get<any>(bldUrl).pipe(first()).subscribe(x=>{
            if(x?.userConfirmedNoItems){
                this.userConfirmedNoItemsList$.next(x.userConfirmedNoItems);
            }

            if(x?.formSectionSummaryList && x?.formSectionSummaryList?.length > 0){
                for(let kvp of x.formSectionSummaryList){
                    // using checkFormHiddenTypeSections here (3rd param) in case previous save was from a different form type
                    let summary = kvp.value as LgvFormlyModel.GroupSummary;
                    summary.refreshedInApp = false;
                    this.setChangeSummaryByKey(kvp.key, kvp.value, true);
                }
            }
            else{
                console.log(`Empty formSectionSummaryList`);
            }
            this.isLoadingFormState$.next(false);
            this.manageCoBorrowerStatus(this.isApplyingForCoBorrower$.value);

        }, (err) => {
            this.notificationSvc.error(`There was an error loading the application state`);
            console.log(err);
            this.isLoadingFormState$.next(false);
        });


        this.dataModel$.next(formModel);

        //update terms with default value
        this.jsonLoader.lendingPadLoanAppJson$.pipe(first(f=> !!f)).subscribe(x=>{
            const termsWithDefaultValue = x.terms.map(term => {
                return {
                    ...term,
                    defaultValue: this.getTermValue(term.key)
                }
            });
            this.terms$.next(termsWithDefaultValue);
        });
        
        this.initRespaChecks();
        this.isLoadingModel$.next(false);

    }

    public saveForm(submitForm: boolean = false, isRespa: boolean = false){

        let formModel = structuredClone(this.dataModel$.value);
        let curDBModel = structuredClone(this.savedLoanApp$.value);
        let modelToSave = structuredClone(curDBModel);

        // Copy Loan Info
        if(formModel.loanApp.loanInfo){
            modelToSave.loanInfo = structuredClone(formModel.loanApp.loanInfo);
        }

        // Handle Borrowers
        modelToSave.loanInfo.borrowers = [];
        if (formModel.loanApp.borrower) {
            if (!formModel?.loanApp?.loanInfo?.formCustomFields?.IsApplyingForCoBorrower) {
                formModel.loanApp.borrower.coBorrower = null;
            }
            else if (formModel?.loanApp?.borrower?.coBorrower?.currentAddress?.isTheSameAsBorrower) {
                formModel.loanApp.borrower.coBorrower.currentAddress = {
                    street: formModel?.loanApp?.borrower?.currentAddress?.street,
                    city: formModel?.loanApp?.borrower?.currentAddress?.city,
                    state: formModel?.loanApp?.borrower?.currentAddress?.state,
                    zipCode: formModel?.loanApp?.borrower?.currentAddress?.zipCode,
                    residencyType: formModel?.loanApp?.borrower?.currentAddress?.residencyType,
                    residencyDurationInMonths: formModel?.loanApp?.borrower?.currentAddress?.residencyDurationInMonths,
                    monthlyRent: formModel?.loanApp?.borrower?.currentAddress?.monthlyRent
                }
            }

            // add to owned property array if current address is owned
           formModel.loanApp.borrower =  this.mapCurrentOwnedAddressToSave(formModel.loanApp.borrower, true);
           if (!!formModel?.loanApp?.borrower?.coBorrower)
            {
                formModel.loanApp.borrower.coBorrower =  this.mapCurrentOwnedAddressToSave(formModel.loanApp.borrower.coBorrower, false);
            }

            //convert demographics 
            formModel.loanApp.borrower = this.mapDemographicsToSave(formModel.loanApp.borrower);
            if (!!formModel?.loanApp?.borrower?.coBorrower)
            {
                formModel.loanApp.borrower.coBorrower = this.mapDemographicsToSave(formModel.loanApp.borrower.coBorrower);
            }

            // split borrower and co-borrower arrays
            formModel.loanApp.borrower = this.splitBorrowerAndCoBorrowerArraysToSave(formModel.loanApp.borrower, 'liabilities', 'tempLiabilities');
            formModel.loanApp.borrower = this.splitBorrowerAndCoBorrowerArraysToSave(formModel.loanApp.borrower, 'employments', 'tempEmployments');
            formModel.loanApp.borrower = this.splitBorrowerAndCoBorrowerArraysToSave(formModel.loanApp.borrower, 'otherIncomes', 'tempOtherIncomes');
            formModel.loanApp.borrower = this.splitBorrowerAndCoBorrowerArraysToSave(formModel.loanApp.borrower, 'assets', 'tempAssets');
            formModel.loanApp.borrower = this.splitBorrowerAndCoBorrowerArraysToSave(formModel.loanApp.borrower, 'ownedProperties', 'tempOwnedProperties', 'ownedBy');

            //handle SSN borrower.  If it doesn't start w/ '***', then the user made and edit
            if(formModel.loanApp?.borrower?.encryptedSsn && !formModel.loanApp.borrower.ssn.startsWith('***')){
                delete formModel.loanApp.borrower.encryptedSsn;
            }
            //handle SSN coBorrower
            if(formModel.loanApp.borrower?.coBorrower?.encryptedSsn && !formModel.loanApp.borrower.coBorrower?.ssn?.startsWith('***')){
                delete formModel.loanApp.borrower.coBorrower.encryptedSsn;
            }
            if(!formModel?.loanApp?.loanInfo?.formCustomFields?.IsApplyingForCoBorrower) {
                delete formModel.loanApp.borrower.coBorrower;
            }
            modelToSave.loanInfo.borrowers.push(formModel.loanApp.borrower);

            // convert FORM  employment years / months to DB months
            if(modelToSave.loanInfo.borrowers.length > 0){
                for(let b of modelToSave.loanInfo.borrowers){
                    if(b.employments?.length > 0){
                        for(let e of b?.employments){
                            let years = e?.formWorkYears ? +e?.formWorkYears : 0;
                            let months = e?.formWorkMonths ? +e?.formWorkMonths : 0;
                            if(!!e) {
                                e.lineOfWorkInMonths = (years * 12) + months;
                            }
    
                            // ***** don't delete because it will also be deleted int the form unless we do a "deep copy"  *****
                            // if(e.formWorkYears){ delete e.formWorkYears }
                            // if(e.formWorkMonths){ delete e.formWorkMonths }
                        }
                    }

                    if(b?.coBorrower?.employments?.length > 0){
                        for(let e of b?.coBorrower?.employments){
                            let years = e?.formWorkYears ? +e?.formWorkYears : 0;
                            let months = e?.formWorkMonths ? +e?.formWorkMonths : 0;
                            e.lineOfWorkInMonths = (years * 12) + months;
    
                            // ***** don't delete because it will also be deleted int the form unless we do a "deep copy"  *****
                            // if(e.formWorkYears){ delete e.formWorkYears }
                            // if(e.formWorkMonths){ delete e.formWorkMonths }
                        }
                    }
                }
            }
        }

        // Process custom fields
        modelToSave.loanInfo.customFields = [];
        if(formModel.loanApp.loanInfo?.formCustomFields){
            formModel.loanApp.loanInfo.formCustomFields = {
                ...formModel.loanApp.loanInfo?.formCustomFields, 
                IsShortApp: this.formType == FormType.PreQual || this.showRespaTrigger$.value,
                IsFullApp: this.formType == FormType.FullLoanApp && !this.showRespaTrigger$.value
            };
            let fields = formModel.loanApp.loanInfo?.formCustomFields;

            Object.keys(formModel.loanApp.loanInfo?.formCustomFields).forEach(key=>{
                let cf = this.lgvLoanAppSvc.buildCustomField(key, fields[key]);
                if(cf){
                    modelToSave.loanInfo.customFields.push(cf);
                }
            });
        }
        
        if(modelToSave.loanInfo["formCustomFields"]){
            delete modelToSave.loanInfo["formCustomFields"];
        }

        if(formModel.loanApp.loanInfo?.subjectPropertyAddress?.tbd){
            modelToSave.loanInfo.subjectPropertyAddress.street = "tbd"
        }


        // Re-add the assignments.  May not be needed.  Up to Raffy
        modelToSave.loanInfo.assignments = structuredClone(curDBModel.loanInfo.assignments);

        let formStateModel = {
            formSectionSummaryList: this.buildUISnapshot(this.appNode$.value.appObj.fieldGroup, []),
            userConfirmedNoItems: this.userConfirmedNoItemsList$.value
        };


        if(modelToSave.id){
            // save loan app


            // we are calling save on each navigation, so only save if there is an actual change to the payload model
            let anyLoanApplicationModelChanges = true;
            if(this.previouslySavedModel){
                let modelToSaveStr = JSON.stringify(modelToSave);

                if(this.previouslySavedModel === modelToSaveStr){
                    // console.log('====== Skipping Loan Application Save - OBJECTS ARE EQUAL =========');
                    anyLoanApplicationModelChanges = false;
                }
            }

            if(anyLoanApplicationModelChanges || submitForm){
                this.previouslySavedModel = JSON.stringify(modelToSave);

                let bldUrl = `${env.LgvApiBaseUrl}/loanapplications/${modelToSave.id}/${submitForm ? 'submit':'save'}`;
                if(this.clientId){
                    bldUrl = bldUrl + `?clientId=${this.clientId}`;
                }

                let request: Observable<any> = null;
                if(submitForm){
                    request = this.http.post<any>(bldUrl, modelToSave).pipe(first());
                }else{
                    console.log('here model',modelToSave)
                    request = this.http.put<any>(bldUrl, modelToSave).pipe(first());
                }
                request.pipe(
                    mergeMap((x) => {
                        if(this.clientId) {
                            return this.lgvLoanAppSvc.fetchThisLoanApp(modelToSave.id, this.clientId);

                        } else {
                            return this.lgvLoanAppSvc.fetchThisLoanApp(modelToSave.id);
                        }
                    })
                ).subscribe(dbModel =>{
                    this.notificationSvc.success(`Loan Application ${submitForm ? 'Submitted': 'Saved'}`);
                    if(submitForm){ 
                        this.OnLoanSubmitted.emit(true); 
                    } else {
                        //this.updateFromDB(modelToSave.id, dbModel);
                    }
                    

                }, (err) => {

                    if(submitForm){ 
                        this.OnLoanSubmitted.emit(false); 
                    } 
                    
                    this.notificationSvc.error(`There was an error ${submitForm ? 'submitting': 'saving'} the loan app`);
                    console.log(err);
                });
            }



            // we are calling save on each navigation, so only save if there is an actual change to the payload model
            let anyFormStateChanges = true;
            if(this.previouslySavedFormState){
                let formStateToSaveStr = JSON.stringify(formStateModel);

                if(this.previouslySavedFormState === formStateToSaveStr){
                    // console.log('====== Skipping FormState Save - OBJECTS ARE EQUAL =========');
                    anyFormStateChanges = false;
                }
            }

            if(anyFormStateChanges){
                this.previouslySavedFormState = JSON.stringify(formStateModel);

                // save form state
                let bldUrl = `${env.LgvApiBaseUrl}/loanapplications/${modelToSave.id}/formstate`;
                if(this.clientId){
                    bldUrl = bldUrl + `?clientId=${this.clientId}`;
                }
                this.http.put<any>(bldUrl, formStateModel).pipe(first()).subscribe(x=>{
                }, (err) => {
                    this.notificationSvc.error(`There was an error saving the loan state`);
                    console.log(err);
                });
            }



        }
    }

    isSameAddresses(address1, address2): boolean {
        if((!!address1 && !(!!address2)) || (!!address2 && !(!!address1))){
            return false;
        }
        
        return address1?.street?.trim().toLowerCase() == address2?.street?.trim().toLowerCase() && 
        address1?.city?.trim().toLowerCase() == address2?.city?.trim().toLowerCase() && 
        address1?.state?.trim().toLowerCase() == address2?.state?.trim().toLowerCase() && 
        address1?.zipCode?.trim().toLowerCase() == address2?.zipCode?.trim().toLowerCase() &&
        address1?.residencyType?.trim().toLowerCase() == address2?.residencyType?.trim().toLowerCase() &&
        address1?.residencyDurationInMonths == address2?.residencyDurationInMonths
    }

    splitBorrowerAndCoBorrowerArraysToSave(borrower, fieldKey, tempFieldKey, identifierFieldName = 'appliesTo') {
        const coBorrowerArray = borrower[tempFieldKey]?.filter(op=> op && op[identifierFieldName] && op[identifierFieldName] === 'CoBorrower');
        const borrowerArray = borrower[tempFieldKey]?.filter(op=> !op || (!op[identifierFieldName] || op[identifierFieldName] === 'Borrower') );
        if(borrowerArray?.length > 0) {
            borrower = {
                ...borrower,
                [fieldKey]: borrowerArray
            };
        }

        if(coBorrowerArray?.length > 0) {
            borrower.coBorrower = {
                ...borrower.coBorrower,
                [fieldKey]: coBorrowerArray
            };
        }
        
        delete borrower[tempFieldKey];
        return borrower;
    }

    mergeBorrowerAndCoBorrowerArraysFromDB(borrower, fieldKey, tempFieldKey, identifierFieldName = 'appliesTo') {
        if(borrower[fieldKey]?.length > 0) {
            borrower[fieldKey] = borrower[fieldKey]?.map(op => {
                return {
                    ...op,
                    [identifierFieldName]: 'Borrower'
                } 
            });
        }
        
        if (borrower?.coBorrower && borrower?.coBorrower[fieldKey]?.length > 0){
            borrower.coBorrower[fieldKey] = borrower?.coBorrower[fieldKey]?.map(op => {
                return {
                    ...op,
                    [identifierFieldName]: 'CoBorrower'
                } 
            });
        }
        if (borrower && !!borrower?.coBorrower && borrower?.coBorrower[fieldKey]?.length > 0 && borrower[fieldKey]?.length > 0) {
            borrower = {
                ...borrower,
                [tempFieldKey]: borrower[fieldKey]?.concat(borrower.coBorrower[fieldKey])
            }
        } else if (!!borrower?.coBorrower && borrower?.coBorrower[fieldKey]?.length > 0) {
            borrower = {
                ...borrower,
                [tempFieldKey]: borrower.coBorrower[fieldKey]
            }
        } else if (borrower[fieldKey]?.length > 0) {
            borrower = {
                ...borrower,
                [tempFieldKey]: borrower[fieldKey]
            }
        }
        
        return borrower;
    }

    mapCurrentOwnedAddressFromDB(borrower, isBorrower) {
        const currentOwnedIndex = borrower?.ownedProperties?.findIndex(p => p.isCurrent);
                
                if(currentOwnedIndex >=0) {
                    const currentOwnedProperty = borrower?.ownedProperties[currentOwnedIndex];
                    borrower.currentAddress.ownedBy = isBorrower ? 'Borrower' : 'CoBorrower';
                    borrower.currentAddress.marketValue = currentOwnedProperty.marketValue;
                    borrower.currentAddress.dispositionStatus = currentOwnedProperty.dispositionStatus;
                    borrower.currentAddress.occupancy = currentOwnedProperty.occupancy;
                    borrower.currentAddress.maintenanceExpense = currentOwnedProperty.maintenanceExpense;
                    borrower.currentAddress.monthlyRentalGross = currentOwnedProperty.monthlyRentalGross;
                    borrower.currentAddress.street = currentOwnedProperty.street;
                    borrower.currentAddress.city = currentOwnedProperty.city;
                    borrower.currentAddress.state = currentOwnedProperty.state;
                    borrower.currentAddress.zipCode = currentOwnedProperty.zipCode;
                    borrower.currentAddress.hasMortgage = !!currentOwnedProperty.liability;
                    borrower.currentAddress.residencyType = 'Own';
                }
        return borrower;
    }

    mapCurrentOwnedAddressToSave(borrower, isBorrower) {
        if(borrower?.currentAddress?.residencyType == 'Own') {
            if(borrower?.ownedProperties) {
                borrower.ownedProperties.push({
                    isCurrent: true,
                    ownedBy: isBorrower ? 'Borrower': 'CoBorrower',
                    marketValue: borrower.currentAddress.marketValue,
                    dispositionStatus: borrower.currentAddress.dispositionStatus,
                    occupancy: borrower.currentAddress.occupancy,
                    maintenanceExpense: borrower.currentAddress.maintenanceExpense,
                    monthlyRentalGross: borrower.currentAddress.monthlyRentalGross,
                    street: borrower.currentAddress.street,
                    city: borrower.currentAddress.city,
                    state: borrower.currentAddress.state,
                    zipCode: borrower.currentAddress.zipCode,
                    liability: borrower.currentAddress.hasMortgage ? {
                        name: "MortgageLoan"
                    } : null
                });
            } else {
                borrower.ownedProperties = [{
                    isCurrent: true,
                    ownedBy: isBorrower ? 'Borrower': 'CoBorrower',
                    marketValue: borrower.currentAddress.marketValue,
                    dispositionStatus: borrower.currentAddress.dispositionStatus,
                    occupancy: borrower.currentAddress.occupancy,
                    maintenanceExpense: borrower.currentAddress.maintenanceExpense,
                    monthlyRentalGross: borrower.currentAddress.monthlyRentalGross,
                    street: borrower.currentAddress.street,
                    city: borrower.currentAddress.city,
                    state: borrower.currentAddress.state,
                    zipCode: borrower.currentAddress.zipCode,
                    liability: borrower.currentAddress.hasMortgage ? {
                        name: "MortgageLoan"
                    } : null
                }];
            }
        }

        return borrower;
    }

    mapDemographicsToSave(borrower) {
        if (!borrower?.demographics) {
            borrower = {
                ...borrower,
                demographics: {}
            };
        }
        if(borrower.demographics?.gender?.name === 'hasRefusedToDiscloseGender') {
            borrower.demographics.hasRefusedToDiscloseGender = true;
            delete borrower.demographics.gender;
        } else if (borrower.demographics?.gender?.name &&
            borrower.demographics?.gender?.name !== 'hasRefusedToDiscloseGender'){
            borrower.demographics.gender = borrower.demographics?.gender;
            borrower.demographics.hasRefusedToDiscloseGender = false;
        }

        if (borrower.tempEthnicity) {
            borrower.demographics.hasRefusedToDiscloseEthnicity =
                borrower.tempEthnicity.doNotWishToProvide
            borrower.demographics.ethnicity = {
                name: '',
                origins: []
            };
            if(borrower.tempEthnicity?.parentEthnicity == 'hispanicOrLatino') {
                borrower.demographics.ethnicity.name = "HispanicOrLatino"
                if(borrower.tempEthnicity?.mexican) {
                    borrower?.demographics?.ethnicity?.origins?.push({
                        name: "Mexican"
                    });
                }
                if(borrower.tempEthnicity?.cuban) {
                    borrower?.demographics?.ethnicity?.origins?.push({
                        name: "Cuban"
                    });
                }
                if(borrower.tempEthnicity?.puertoRican) {
                    borrower.demographics?.ethnicity?.origins?.push({
                        name: "PuertoRican"
                    });
                }
                if(borrower.tempEthnicity?.otherEthnicity) {
                    borrower.demographics.ethnicity.origins.push({
                        name: "Other",
                        otherDescription: borrower?.tempEthnicity?.otherEthnicityValue

                    });
                }
            }

            if(borrower.tempEthnicity?.parentEthnicity == 'notHispanicOrLatino') {
                borrower.demographics.ethnicity.name = "NotHispanicOrLatino"
                delete borrower.demographics.ethnicity.origins;
            }
            delete borrower.tempEthnicity;
        }

        if (borrower.tempRaces) {
            borrower.demographics.hasRefusedToDiscloseRace = 
                borrower.tempRaces.doNotWishToProvide;
            borrower.demographics.races =[];

            if(borrower.tempRaces.americanIndianOrAlaskaNative) {
                borrower.demographics.races.push({
                    name: 'AmericanIndianOrAlaskaNative',
                    additionalDescription: borrower.tempRaces.americanIndianOrAlaskaNativeDescription
                });
            }

            if(borrower.tempRaces.asian) {
                const asianRace = {
                    name: 'Asian',
                    raceDesignations: []
                };

                if(borrower.tempRaces.asianIndian) {
                    asianRace.raceDesignations.push({
                        name: 'AsianIndian'
                    })
                }

                if(borrower.tempRaces.chinese) {
                    asianRace.raceDesignations.push({
                        name: 'Chinese'
                    })
                }

                if(borrower.tempRaces.filipino) {
                    asianRace.raceDesignations.push({
                        name: 'Filipino'
                    })
                }

                if(borrower.tempRaces.japanese) {
                    asianRace.raceDesignations.push({
                        name: 'Japanese'
                    })
                }

                if(borrower.tempRaces.korean) {
                    asianRace.raceDesignations.push({
                        name: 'Korean'
                    })
                }

                if(borrower.tempRaces.vietnamese) {
                    asianRace.raceDesignations.push({
                        name: 'Vietnamese'
                    })
                }

                if(borrower.tempRaces.otherAsian) {
                    asianRace.raceDesignations.push({
                        name: 'OtherAsian',
                        otherDescription: borrower.tempRaces.otherAsianValue
                    })
                }

                borrower.demographics.races.push(asianRace);
            }

            if(borrower.tempRaces.blackOrAfricanAmerican) {
                borrower.demographics.races.push({
                    name: 'BlackOrAfricanAmerican'
                });
            }

            if(borrower.tempRaces.nativeHawaiianOrOtherPacificIslander) {
                const nativeHawaiianOrOtherRace = {
                    name: 'NativeHawaiianOrOtherPacificIslander',
                    raceDesignations: []
                };

                if(borrower.tempRaces.guamanianOrChamorro) {
                    nativeHawaiianOrOtherRace.raceDesignations.push({
                        name: 'GuamanianOrChamorro'
                    })
                }

                if(borrower.tempRaces.nativeHawaiian) {
                    nativeHawaiianOrOtherRace.raceDesignations.push({
                        name: 'NativeHawaiian'
                    })
                }

                if(borrower.tempRaces.samoan) {
                    nativeHawaiianOrOtherRace.raceDesignations.push({
                        name: 'Samoan'
                    })
                }

                if(borrower.tempRaces.otherPacificIslander) {
                    nativeHawaiianOrOtherRace.raceDesignations.push({
                        name: 'OtherPacificIslander',
                        otherDescription: borrower.tempRaces.otherPacificIslanderValue
                    })
                }

                borrower.demographics.races.push(nativeHawaiianOrOtherRace);
            }

            if(borrower.tempRaces.white) {
                borrower.demographics.races.push({
                    name: 'White'
                });
            }

            delete borrower.tempRaces;
        }
        

        return borrower;
    }

    mapDemographicsFromDB(borrower) {
        if(borrower?.demographics?.hasRefusedToDiscloseGender) {
            borrower.demographics.gender = {
                name: 'hasRefusedToDiscloseGender'
            };
        }

        if(borrower?.demographics?.hasRefusedToDiscloseEthnicity & borrower?.tempEthnicity) {
            borrower.tempEthnicity.doNotWishToProvide = true;
            borrower.tempEthnicity = {
                ...borrower.tempEthnicity,
                doNotWishToProvide: true
            };
        }

        if(borrower?.demographics?.hasRefusedToDiscloseRace) {
            borrower.tempRaces = {
                ...borrower.tempRaces,
                doNotWishToProvide: true
            }
        }

        if(borrower?.demographics?.ethnicity) {
            borrower.tempEthnicity = {} as any;
            if(borrower?.demographics?.ethnicity?.name === 'HispanicOrLatino') {
                borrower.tempEthnicity.parentEthnicity = 'hispanicOrLatino';
                borrower.tempEthnicity.mexican = borrower?.demographics?.ethnicity?.origins
                    .includes(x=> x.name.toLowerCase() === 'mexican');
                borrower.tempEthnicity.cuban = borrower?.demographics?.ethnicity?.origins
                    .includes(x=> x.name.toLowerCase() === 'cuban');
                borrower.tempEthnicity.puertoRican = borrower?.demographics?.ethnicity?.origins
                    .includes(x=> x.name.toLowerCase() === 'puertorican');
                borrower.tempEthnicity.otherEthnicity = borrower?.demographics?.ethnicity?.origins
                    .includes(x=> x.name.toLowerCase() === 'other');
                borrower.tempEthnicity.otherEthnicityValue = (borrower?.demographics?.ethnicity?.origins
                .find(x=> x.name.toLowerCase() === 'other'))?.otherDescription;
            } else if(borrower?.demographics?.ethnicity?.name === 'NotHispanicOrLatino') {
                borrower.tempEthnicity.parentEthnicity = 'notHispanicOrLatino';
            }
        }

        if(borrower?.demographics?.races) {
            borrower.tempRaces = {};
             const americanIndianOrAlaskaNative = borrower?.demographics?.races?.find(
                x=> x.name === 'AmericanIndianOrAlaskaNative');
            const asian = borrower?.demographics?.races?.find(
                x=> x.name === 'Asian');
            const blackOrAfricanAmerican = borrower?.demographics?.races?.find(
                x=> x.name === 'BlackOrAfricanAmerican');
            const nativeHawaiianOrOther = borrower?.demographics?.races?.find(
                x=> x.name === 'NativeHawaiianOrOtherPacificIslander');
                const white = borrower?.demographics?.races?.find(
                    x=> x.name === 'White');

            if(americanIndianOrAlaskaNative) {
                borrower.tempRaces.americanIndianOrAlaskaNative = true;
                borrower.tempRaces.americanIndianOrAlaskaNativeDescription = americanIndianOrAlaskaNative?.additionalDescription;
            }

            if (asian) {
                borrower.tempRaces.asian = true;
                borrower.tempRaces.asianIndian = asian.raceDesignations
                    .includes(x=> x.name === 'AsianIndian');
                borrower.tempRaces.chinese = asian.raceDesignations
                    .includes(x=> x.name === 'Chinese');
                borrower.tempRaces.filipino = asian.raceDesignations
                    .includes(x=> x.name === 'Filipino');
                borrower.tempRaces.japanese = asian.raceDesignations
                    .includes(x=> x.name === 'Japanese');
                    borrower.tempRaces.korean = asian.raceDesignations
                    .includes(x=> x.name === 'Korean');
                borrower.tempRaces.vietnamese = asian.raceDesignations
                    .includes(x=> x.name === 'Vietnamese');
                borrower.tempRaces.otherAsian = asian.raceDesignations
                    .includes(x=> x.name === 'OtherAsian');
                borrower.tempRaces.otherAsianValue = asian.raceDesignations
                    .find(x=> x.name === 'OtherAsian')?.otherDescription;
            }

            if (blackOrAfricanAmerican) {
                borrower.tempRaces.blackOrAfricanAmerican = true;
            }

            if (nativeHawaiianOrOther) {
                borrower.tempRaces.nativeHawaiianOrOtherPacificIslander = true;
                borrower.tempRaces.guamanianOrChamorro = nativeHawaiianOrOther.raceDesignations
                    .includes(x=> x.name === 'GuamanianOrChamorro');
                borrower.tempRaces.nativeHawaiian = nativeHawaiianOrOther.raceDesignations
                    .includes(x=> x.name === 'NativeHawaiian');
                borrower.tempRaces.samoan = nativeHawaiianOrOther.raceDesignations
                    .includes(x=> x.name === 'Samoan');
                borrower.tempRaces.otherPacificIslander = nativeHawaiianOrOther.raceDesignations
                    .includes(x=> x.name === 'OtherPacificIslander');
                borrower.tempRaces.otherAsianValue = nativeHawaiianOrOther.raceDesignations
                    .find(x=> x.name === 'OtherPacificIslander')?.otherDescription;
            }

            if (white) {
                borrower.tempRaces.white = true;
            }
        }

        return borrower;
    }

    setChangeSummaryForCurrentLevel(summary: LgvFormlyModel.GroupSummary, arrayIndex = null){
        this.setChangeSummaryForLevel(this.curLevel1$.value, this.curLevel2$.value, summary, arrayIndex);
    }

    buildChangeMapKey(lvl_1:number, lvl_2:number, arrayIndex = null):string{
        return `${lvl_1.toString()}~${lvl_2.toString()}${arrayIndex !== null ? '~' + arrayIndex.toString() : ''}`;
    }

    setChangeSummaryByKey(key:string, summary: LgvFormlyModel.GroupSummary, checkFormTypeHiddenSections = false){
        if(!key){
            console.log('Invalid Key Name in setChangeSummaryByKey()');
            return;
        }

        let parts = key.split('~');

        if(checkFormTypeHiddenSections){
            summary.isHidden = this.isThisSectionHiddenForType(+parts[0], +parts[1]);
        }

        if(parts.length === 2){
            this.setChangeSummaryForLevel(+parts[0], +parts[1], summary);
            this.checkChangesForLevelAndUpdateParent(+parts[0], false );
        }
        else if(parts.length === 3){
            this.setChangeSummaryForLevel(+parts[0], +parts[1], summary, +parts[2]);
            this.checkChangesForLevelAndUpdateParent(+parts[0], true );
        }
        else{
            console.log('Invalid Key in setChangeSummaryByKey()');
        }
        
    }

    isSummaryLevelHidden(l1:number, l2:number){
        let summary = this.getChangeSummaryForLevel(l1, l2);
        return summary && summary.isHidden;
    }

    setChangeSummaryForLevel(l1:number, l2:number, summary: LgvFormlyModel.GroupSummary, arrayIndex = null){
        // console.log(`SET ChangesForLevel  ${l1} - ${l2}  | isHidden: ${summary.isHidden}`);
        let key = this.buildChangeMapKey(l1, l2, arrayIndex);
        let cm = this.changeMap$.value;
        cm.set(key, summary);
        this.changeMap$.next(cm);

        if(l2 > -1 || arrayIndex !== null){
            this.checkChangesForLevelAndUpdateParent(l1, arrayIndex !== null );
        }
    }

    getChangeSummaryForCurrentLevel(): LgvFormlyModel.GroupSummary{
        return this.getChangeSummaryForLevel(this.curLevel1$.value, this.curLevel2$.value);
    }

    observeChangeSummaryForLevel$(l1:number, l2:number, addDefaultIfNotExists:boolean = false, arrayIndex = null): Observable<LgvFormlyModel.GroupSummary>{
        let key = this.buildChangeMapKey(l1, l2, arrayIndex);
        return this.changeMap$.pipe(filter(f=> f!==null),map(cm=> {

            let hasVal = cm.has(key);
            if(!hasVal && addDefaultIfNotExists){
                // cm.set(key, this.newSummary());

                let summary = this.buildEmptySummaryObject();
                if(this.isThisSectionHiddenForType(l1, l2)){
                    summary.isHidden = true;
                }
                this.setChangeSummaryForLevel(l1, l2, summary, arrayIndex);
                this.manageCoBorrowerStatus(this.isApplyingForCoBorrower$.value);
            }
            const summaryList = cm.get(key);
            for (let i = summaryList?.fieldList.length - 1; i >= 0; i--) {
                if ((summaryList?.fieldList[i]?.key.includes('tempEthnicity') || summaryList.fieldList[i]?.key.includes('tempRaces')) &&
                        summaryList?.fieldList[i]?.currentVal == false) { 
                            summaryList?.fieldList.splice(i, 1);
                }
            }
            return summaryList;
        }));
        
    }

    getChangeSummaryKeysForThisLevel(l1:number){
        let rtnVal = [];
        for(let key of this.changeMap$.value.keys()){
            if(key.startsWith(`${l1}~`)){
                rtnVal.push(key);
            }
        }
        return rtnVal;
    }

    getChangeSummaryKeysForArrayItems(l1:number){
        let rtnVal = [];
        for(let key of this.changeMap$.value.keys()){
            if(key.includes(`${l1}~-1~`)){
                rtnVal.push(key);
            }
        }
        return rtnVal;
    }

    updateKeysAfterArrayItemRemoved(l1:number){
        // if array item is deleted, rename the order of the keys from 0, 1, 2, etc...
        let buildList = [];
        let oldKeys = this.getChangeSummaryKeysForThisLevel(l1);
        let cm = this.changeMap$.value;


        let index = 0;
        for(let oldKey of oldKeys){
            let parts = oldKey.split('~');
            if(parts.length !== 3){
                continue;
            }

            let newKey = `${parts[0]}~${parts[1]}~${index}`;
            buildList.push({key: newKey, value:this.getChangeSummaryForKey(oldKey)});
            cm.delete(oldKey);

            index++;
        }

        for(let obj of buildList){
            cm.set(obj.key, obj.value);
        }

        this.changeMap$.next(cm);
    }

    checkChangesForLevelAndUpdateParent(l1:number, isRepeater:boolean = false){
        
        // don't update parent if we are still loading from db
        if(this.isLoadingFormState$.value){
            return;
        }

        let parentSummary = this.getChangeSummaryForLevel(l1, -1);
        if(!parentSummary){
            parentSummary = this.buildEmptySummaryObject();
        }
        let i=0;

        let key = isRepeater ? this.buildChangeMapKey(l1, -1, i) : this.buildChangeMapKey(l1, i);
        let haveObj = this.changeMap$.value.has(key);
        let haveAnyIncomplete = false;
        
        while(haveObj && !haveAnyIncomplete ){
            let child = this.getChangeSummaryForKey(key);

            if(child.sectionStatus !== LgvFormlyModel.FormSectionStatus.Complete && !child.isHidden){
                haveAnyIncomplete = true;
            }

            i++;
            key = isRepeater ? this.buildChangeMapKey(l1, -1, i) : this.buildChangeMapKey(l1, i);
            haveObj = this.changeMap$.value.has(key);
        }

        parentSummary.sectionStatus = haveAnyIncomplete ? LgvFormlyModel.FormSectionStatus.Started : LgvFormlyModel.FormSectionStatus.Complete;
        this.setChangeSummaryForLevel(l1, -1, parentSummary);
    }

    getChangeSummaryForLevel(l1:number, l2:number, addDefaultIfNotExists:boolean = false, arrayIndex:number = null): LgvFormlyModel.GroupSummary{
        // console.log(`getChangesForLevel  ${l1} - ${l2}`);
        let key = this.buildChangeMapKey(l1, l2, arrayIndex);
        return this.getChangeSummaryForKey(key, addDefaultIfNotExists);
    }

    
    getChangeSummaryForKey(key:string, addDefaultIfNotExists:boolean = false): LgvFormlyModel.GroupSummary{

        let hasVal = this.changeMap$.value.has(key);
   
        if(!hasVal){
            if(addDefaultIfNotExists){
                let newSummary = this.buildEmptySummaryObject();
                this.setChangeSummaryByKey(key, newSummary);
                return newSummary;
            }
            else{
                return null;
            }
        }
        else{
            let rtnVal = this.changeMap$.value.get(key);
            // console.log('======== CHANGE SUMMARY ========');
            return rtnVal;
        }
        
    }

    hasChangeSummaryForLevel(lvl_1:number, lvl_2:number, arrayIndex:number = null): boolean {
        // let map = this.changeMap$.value;
        let key = this.buildChangeMapKey(lvl_1, lvl_2, arrayIndex);

        return this.changeMap$.value.has(key);
    }

    hasChangeSummaryForKey(key:string): boolean {
        return this.changeMap$.value.has(key);
    }

    removeSummaryForKey(key:string) {

        if(this.changeMap$.value.has(key)){
            let cm = this.changeMap$.value;
            cm.delete(key);
            this.changeMap$.next(cm);

            let parts = key.split('~');
            if(parts.length === 2){
                this.checkChangesForLevelAndUpdateParent(+parts[0], false );
            }
            else if(parts.length === 3){
                this.updateKeysAfterArrayItemRemoved(+parts[0]);
                this.checkChangesForLevelAndUpdateParent(+parts[0], true );
            }

        }
    }

    buildEmptySummaryObject(): LgvFormlyModel.GroupSummary{
        return {
            sectionStatus: LgvFormlyModel.FormSectionStatus.NotStarted,
            isHidden:false,
            numFields: 0,
            numChanged: 0,
            numRequired: 0,
            numRequiredRemaining: 0,
            fieldList: []
        }
    }

    buildUISnapshot(fields: LgvFormlyModel.Object[], changes:any[]):any[]{
        let rtnVal = [];
        for(let key of this.changeMap$.value.keys()){
            // console.log(key);
            rtnVal.push({
                key:key,
                value: this.changeMap$.value.get(key)
            });
        }
        return rtnVal;
    }

    cssClassFromSummaryStatus(summary: LgvFormlyModel.GroupSummary): string {
        return this.isPreApplicationMode ? this.statusLabelFromSummary(summary) : ''
    }


    statusLabelFromSummary(summary: LgvFormlyModel.GroupSummary): string {
        switch (summary.sectionStatus){
            case LgvFormlyModel.FormSectionStatus.NotStarted: return 'Not Started';
            default: return summary.sectionStatus;
        }
    }

    editButtonLabelFromSummary(summary: LgvFormlyModel.GroupSummary): string {
        switch (summary.sectionStatus){
            case LgvFormlyModel.FormSectionStatus.NotStarted: return 'Start';
            default: return 'Edit';
        }
    }

    //#region Terms
    setTerm(key:string, val:boolean){
        let model = this.dataModel$.value;
        if(!model.loanApp.loanInfo){
            model.loanApp.loanInfo = {};
        }
        if(!model.loanApp.loanInfo.formCustomFields){
            model.loanApp.loanInfo.formCustomFields = {};
        }

        model.loanApp.loanInfo.formCustomFields[key] = val ? 'Yes' : 'No';

        this.dataModel$.next(model);
        if(this.saveOnEachNavigation){
            this.saveForm();
        }
    }

    getTermValue(key: string): boolean | string {
        let rtnVal = false;

        let customFields = this.dataModel$.value?.loanApp?.loanInfo?.formCustomFields;

        if (customFields && customFields[key]) {
            if (!this.isPreApplicationMode) {
                customFields[key] = 'Yes';
                return true;
            }
            else {
                rtnVal = customFields[key] === 'Yes' || customFields[key];
            }
        }
        return rtnVal;
        
    }
    //#endregion Terms

    //#region ConfirmedNoItems
    addConfirmedNoItems(item: LgvFormlyModel.UserConfirmedNoItem){
        this.userConfirmedNoItemsList$.value.push(item);
    }

    haveUserConfirmedNoItems(item: LgvFormlyModel.UserConfirmedNoItem):boolean {
        return this.userConfirmedNoItemsList$.value.findIndex(x => x.changeSummaryKey === item.changeSummaryKey && x.modelKey === item.modelKey) > -1;
    }
    removeConfirmedNoItems(item: LgvFormlyModel.UserConfirmedNoItem){
        let tmpArray = this.userConfirmedNoItemsList$.value;
        let remIndex = tmpArray.findIndex(x => x.changeSummaryKey === item.changeSummaryKey && x.modelKey === item.modelKey);
        if (remIndex > -1) {
          tmpArray.splice(remIndex, 1);
          this.userConfirmedNoItemsList$.next(tmpArray);
        }
    }

    //#endregion ConfirmedNoItems


    //#region Manage Form Type Hidden Sections

    isThisSectionHiddenForType(l1:number, l2:number): boolean{
        let node = this.getThisFieldGroupObj(l1, l2);
        return (this.formType === FormType.PreQual && node?.appObj?.props?.hideForPreQual) 
        || (this.formType === FormType.FullLoanApp && node?.appObj?.props?.hideForFullLoanApp);
    }

    manageHiddenSectionsForFormType(schema:LgvFormlyModel.Object[]) {

        if (schema && schema?.length) {
            let index_1 = 0;
            for (let node of schema) {
                
                if ((this.formType === FormType.PreQual && node.props?.hideForPreQual)
                || (this.formType === FormType.FullLoanApp && node.props?.hideForFullLoanApp) ) {
                    let summary = this.buildEmptySummaryObject();
                    summary.isHidden = true;
                    this.setChangeSummaryForLevel(index_1, -1, summary);
                } 

                if (node?.fieldGroup && node.fieldGroup?.length > 0) {
                    let index_2 = 0;
                    for (let n2 of node.fieldGroup) {

                        if ((this.formType === FormType.PreQual && n2.props?.hideForPreQual)
                        || (this.formType === FormType.FullLoanApp && n2.props?.hideForFullLoanApp)) {
                            let summary = this.buildEmptySummaryObject();
                            summary.isHidden = true;
                            this.setChangeSummaryForLevel(index_1, index_2, summary);
                        }

                        // update the schema to hide field level items with "hideForPreQual" or "hideForFullLoanApp"
                        // field level items are managed by Formly, so use expressions.hide
                        for(let field of n2.fieldGroup){

                            if (
                                (this.formType === FormType.PreQual && field.props?.hideForPreQual)
                                || (this.formType === FormType.FullLoanApp && field.props?.hideForFullLoanApp)) {
                                field.expressions = {
                                    hide: "true"
                                }
                            } 
                        }

                        index_2++;
                    }
                }
                index_1++;
            }
        }
    }

    //#endregion Manage Form Type Hidden Sections

    //#region CoBorrower Status
    manageCoBorrowerStatus(hasCoBorrower: boolean){

        // Wipe out any existing model data to ensure that it is not sent to back end???

        // Update changeSummaryMap for targeted sections
        let schema = this.schema$.value;
        let outerSectionsToCheck = ['aboutYou', 'declarations', 'demographics'];
        let innerSectionsToCheck = ['coBorrowerHistory', 'coBorrowerDeclarations', 'coBorrowerDemographics'];

        if(schema && schema?.length){
            let index_1 = 0;
            for(let node of schema){
                let outerSectionID = node?.props?.lgvSectionId;
                if(outerSectionID && outerSectionsToCheck.includes(outerSectionID))
                {
                    if(node?.fieldGroup && node.fieldGroup?.length > 0){
                        let index_2 = 0;
                        for(let n2 of node.fieldGroup){
                            let innerSectionID = n2?.props?.lgvSectionId;
                            if(innerSectionID && innerSectionsToCheck.includes(innerSectionID)){
                                let summary_2 = this.getChangeSummaryForLevel(index_1, index_2, true);
                                if(summary_2){
                                    summary_2.isHidden = !hasCoBorrower;
                                    
                                    this.setChangeSummaryForLevel(index_1, index_2, summary_2);
                                    this.checkChangesForLevelAndUpdateParent(index_1);

                                    // console.log(`Update: ${outerSectionID}.${innerSectionID} => hidden: ${summary_2.isHidden}`);
                                }
                            }
                            index_2++;
                        }
                    }
                }
                index_1++;
            }
        }
    }

    //#endregion CoBorrower Status
}

export enum FormType {
    FullLoanApp = 'FullLoanApp', PreQual = 'PreQual'
}
